import { z } from 'zod'
import { leaveTypeIdSchema } from './leave.js'

// ── /assist/apply ────────────────────────────────────────────
export const applyAssistantRequestSchema = z.object({
  employeeId: z.guid(),
  draft: z.object({
    type: leaveTypeIdSchema,
    startDate: z.string().date(),
    endDate: z.string().date(),
    halfDay: z.boolean(),
    reason: z.string().optional(),
  }),
  context: z
    .object({
      hasOverlap: z.boolean().default(false),
      hitsBlackout: z.boolean().default(false),
      coversPublicHoliday: z.boolean().default(false),
      teamCoveragePct: z.number().nullable().default(null),
    })
    .optional(),
})

export type ApplyAssistantRequest = z.infer<typeof applyAssistantRequestSchema>

export const applyAssistantResponseSchema = z.object({
  warnings: z.array(z.string()),
  suggestions: z.array(z.string()),
  reasonDraft: z.string().nullable(),
})

export type ApplyAssistantResponse = z.infer<typeof applyAssistantResponseSchema>

// ── /assist/review ───────────────────────────────────────────
export const requestAdvisorRequestSchema = z.object({
  leaveId: z.guid(),
  managerId: z.guid(),
})

export type RequestAdvisorRequest = z.infer<typeof requestAdvisorRequestSchema>

export const requestAdvisorResponseSchema = z.object({
  recommendation: z.enum(['approve', 'reject', 'discuss']),
  rationale: z.string(),
  conflicts: z.array(z.string()),
  priorityFitScore: z.number().min(0).max(1),
})

export type RequestAdvisorResponse = z.infer<typeof requestAdvisorResponseSchema>

// ── /assist/draft ────────────────────────────────────────────
export const policyDrafterRequestSchema = z.object({
  topic: z.string().min(1),
  audience: z.enum(['all', 'managers', 'hr', 'team']),
  tone: z.enum(['neutral', 'warm', 'formal']).default('warm'),
  context: z.string().optional(),
})

export type PolicyDrafterRequest = z.infer<typeof policyDrafterRequestSchema>

export const policyDrafterResponseSchema = z.object({
  subject: z.string(),
  body: z.string(),
})

export type PolicyDrafterResponse = z.infer<typeof policyDrafterResponseSchema>

// ── /leaves/:id/summary ──────────────────────────────────────
export const leaveSummaryToolCallSchema = z.object({
  toolId: z.string(),
  summary: z.string(),
  computedAt: z.string(),
  renderedSql: z.string().nullable(),
})

export type LeaveSummaryToolCall = z.infer<typeof leaveSummaryToolCallSchema>

export const leaveSummaryResponseSchema = z.object({
  summary: z.string(),
  computedAt: z.string(),
  toolCalls: z.array(leaveSummaryToolCallSchema),
})

export type LeaveSummaryResponse = z.infer<typeof leaveSummaryResponseSchema>

// ── /employees/:id/conversation-draft (Slice A3) ─────────────
export const conversationDraftRequestSchema = z.object({
  patternKinds: z.array(z.string()).max(10).default([]),
  notes: z.string().nullish(),
  windowDays: z.number().int().min(7).max(365).default(90),
})

export type ConversationDraftRequest = z.infer<typeof conversationDraftRequestSchema>

export const conversationDraftSchema = z.object({
  title: z.string().min(1),
  opening: z.string().min(1),
  talkingPoints: z.array(z.string()).min(1).max(8),
  questionsToAsk: z.array(z.string()).max(6).default([]),
  toneWarning: z.string().nullable(),
  nextStep: z.string().min(1),
})

export type ConversationDraft = z.infer<typeof conversationDraftSchema>

export const conversationDraftResponseSchema = z.object({
  invocationId: z.string().nullable(),
  draft: conversationDraftSchema,
  computedAt: z.string(),
  toolCalls: z.array(leaveSummaryToolCallSchema),
})

export type ConversationDraftResponse = z.infer<typeof conversationDraftResponseSchema>

export const conversationSendSlackResponseSchema = z.object({
  delivered: z.boolean(),
  slackTs: z.string().nullable(),
  slackChannel: z.string().nullable(),
  error: z.string().nullable(),
})

export type ConversationSendSlackResponse = z.infer<
  typeof conversationSendSlackResponseSchema
>

// ── /ai/policy-qa (Slice C3) ─────────────────────────────────
export const policyQAHistoryTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
})

export const policyQACitationSchema = z.object({
  toolId: z.string(),
  summary: z.string(),
  renderedSql: z.string().nullable(),
  /**
   * Truncated JSON of the tool's returned value (id-shaped fields
   * stripped). Optional — older responses, and tools whose payload is
   * unserialisable, omit it. Consumed by the role-catalogue eval judge
   * and the "Show source" expander to verify claims against real data.
   */
  preview: z.string().nullable().optional(),
  /** Sprint 6 F12b — policy graph provenance fields from the policy table. */
  policy_id: z.string().optional(),
  version: z.number().int().positive().optional(),
  prose_en: z.string().optional(),
  source_ref: z.string().nullable().optional(),
})

export const policyQAResponseSchema = z.object({
  answer: z.string(),
  citations: z.array(policyQACitationSchema),
  computedAt: z.string(),
})

export type PolicyQACitation = z.infer<typeof policyQACitationSchema>
export type PolicyQAResponse = z.infer<typeof policyQAResponseSchema>

// ── /evidence-prompt (Slice C2) ──────────────────────────────
export const evidencePromptRequestSchema = z.object({
  leaveId: z.guid(),
  leaveType: z.string(),
  startDate: z.string().date(),
  endDate: z.string().date(),
  daysCount: z.number().int().min(1).max(365),
  hasEvidence: z.boolean(),
})

export type EvidencePromptRequest = z.infer<typeof evidencePromptRequestSchema>

export const evidencePromptResponseSchema = z.object({
  shouldPrompt: z.boolean(),
  severity: z.enum(['info', 'warning']),
  title: z.string(),
  body: z.string(),
  computedAt: z.string(),
})

export type EvidencePromptResponse = z.infer<typeof evidencePromptResponseSchema>

// ── /leaves/counterfactual-preview (Slice C1) ────────────────
export const counterfactualOptionSchema = z.object({
  label: z.string(),
  startDate: z.string().date(),
  endDate: z.string().date(),
  outCount: z.number().int(),
})

export type CounterfactualOption = z.infer<typeof counterfactualOptionSchema>

export const counterfactualPreviewResponseSchema = z.object({
  proposed: counterfactualOptionSchema,
  shiftMinusOne: counterfactualOptionSchema,
  shiftPlusOne: counterfactualOptionSchema,
  computedAt: z.string(),
})

export type CounterfactualPreviewResponse = z.infer<typeof counterfactualPreviewResponseSchema>

// ── /leaves/window-recommendations ───────────────────────────
export const currentWindowSchema = z.object({
  startDate: z.string().date(),
  endDate: z.string().date(),
  absencePct: z.number(),
  blocked: z.boolean(),
  outCount: z.number().int(),
  capPct: z.number().int(),
  // Balance fields — null/undefined when the balance row is unavailable.
  requestedCredits: z.number().int().default(0),
  remainingCredits: z.number().int().nullable().default(null),
  overflowsBalance: z.boolean().default(false),
  elapsedBasis: z.boolean().default(false),           // true when leave type counts calendar days
  hardCapped: z.boolean().default(false),             // true for non-annual types that block on overflow
})

export type CurrentWindow = z.infer<typeof currentWindowSchema>

export const alternativeWindowSchema = z.object({
  startDate: z.string().date(),
  endDate: z.string().date(),
  lengthDays: z.number().int(),
  absencePct: z.number(),
  recommended: z.boolean(),
  why: z.string().nullable().optional(),
})

export type AlternativeWindow = z.infer<typeof alternativeWindowSchema>

export const windowRecommendationResponseSchema = z.object({
  current: currentWindowSchema,
  alternatives: z.array(alternativeWindowSchema),
})

export type WindowRecommendationResponse = z.infer<typeof windowRecommendationResponseSchema>

// ── /leaves/:id/cancel-draft (Slice B2) ──────────────────────
export const cancelLeaveDraftRequestSchema = z.object({
  reason: z.string().min(1).max(2000),
})

export type CancelLeaveDraftRequest = z.infer<typeof cancelLeaveDraftRequestSchema>

export const cancelLeaveDraftResponseSchema = z.object({
  subject: z.string(),
  body: z.string(),
  computedAt: z.string(),
})

export type CancelLeaveDraftResponse = z.infer<typeof cancelLeaveDraftResponseSchema>

// ── /insights/quarterly-read (Slice B1) ──────────────────────
export const quarterlyReadSectionSchema = z.object({
  title: z.string(),
  body: z.string(),
  citations: z.array(z.string()),
})

export type QuarterlyReadSection = z.infer<typeof quarterlyReadSectionSchema>

export const anomalySchema = z.object({
  kind: z.enum(['outlier_vs_team', 'friday_monday_cluster', 'accrual_velocity']),
  severity: z.enum(['info', 'warn', 'high']),
  subject: z.string(),
  detail: z.string(),
  value: z.number(),
  threshold: z.number(),
})

export type Anomaly = z.infer<typeof anomalySchema>

export const quarterlyReadResponseSchema = z.object({
  headline: z.string(),
  sections: z.array(quarterlyReadSectionSchema),
  computedAt: z.string(),
  toolCalls: z.array(leaveSummaryToolCallSchema),
  anomalies: z.array(anomalySchema).default([]),
  /** Total approved leave requests in the requested window (deterministic count). */
  totalLeavesTaken: z.number().int().nonnegative().default(0),
})

export type QuarterlyReadResponse = z.infer<typeof quarterlyReadResponseSchema>

// ── /insights/qa-triage (Slice C3 follow-up) ─────────────────

/**
 * AI provenance envelope — carried on triage rows and other AI surfaces.
 * model: the model id that produced the answer.
 * latency: round-trip ms from api to ai-service.
 * source: stable string id for the prompt/version (e.g. "policy-qa-v1").
 * promptId: optional versioned prompt identifier.
 */
export const aiProvenanceSchema = z.object({
  model: z.string(),
  latency: z.number().nonnegative(),
  source: z.string(),
  promptId: z.string().optional(),
})
export type AiProvenance = z.infer<typeof aiProvenanceSchema>

export const qaTriageItemSchema = z.object({
  invocationId: z.string(),
  askedAt: z.string(),
  questionPreview: z.string(),
  answerPreview: z.string(),
  /** 0.0–1.0 confidence from the model response (logprob-derived or heuristic). */
  confidence: z.number().min(0).max(1).default(0.5),
  /** AI provenance for Rule 02 compliance in the "Why flagged" block. */
  provenance: aiProvenanceSchema.optional(),
})

export type QaTriageItem = z.infer<typeof qaTriageItemSchema>

export const qaTriageResponseSchema = z.object({
  items: z.array(qaTriageItemSchema),
  dbEnabled: z.boolean(),
})

export type QaTriageResponse = z.infer<typeof qaTriageResponseSchema>

export const qaTriageResolveRequestSchema = z.object({
  note: z.string().max(1000).optional(),
})

export type QaTriageResolveRequest = z.infer<typeof qaTriageResolveRequestSchema>

export const qaTriageResolveResponseSchema = z.object({
  ok: z.boolean(),
  reason: z.string().nullable().optional(),
})

export type QaTriageResolveResponse = z.infer<typeof qaTriageResolveResponseSchema>

// ── /analyze/patterns ────────────────────────────────────────
export const patternLeaveRowSchema = z.object({
  id: z.guid(),
  type: leaveTypeIdSchema,
  startDate: z.string().date(),
  endDate: z.string().date(),
  halfDay: z.boolean(),
  status: z.string(),
  isEmergency: z.boolean(),
})
export type PatternLeaveRow = z.infer<typeof patternLeaveRowSchema>

export const PatternKind = z.enum([
  'frequent_sick_leave',
  'poor_planning_emergency',
  'emergency_approaching_limit',
  'unpaid_leave_taken',
  'frequent_half_days',
  'long_weekend_pattern',
  'no_leave_in_window',
  'high_early_utilisation',
])
export type PatternKind = z.infer<typeof PatternKind>

export const patternsRequestSchema = z.object({
  employeeId: z.guid(),
  windowDays: z.number().int().min(7).max(365).default(90),
  leaves: z.array(patternLeaveRowSchema).default([]),
  publicHolidays: z.array(z.string().date()).default([]),
  hireDate: z.string(),                // ISO date
  annualEntitlementDays: z.number().int().min(0).max(365),
})

export type PatternsRequest = z.infer<typeof patternsRequestSchema>

export const patternsResponseSchema = z.object({
  patterns: z.array(
    z.object({
      kind: PatternKind,
      severity: z.enum(['low', 'medium', 'high']),
      possibleSignal: z.string(),
      recommendation: z.string(),
      evidence: z.object({
        count: z.number().int(),
        windowDays: z.number().int(),
        sampleLeaveIds: z.array(z.guid()),
      }),
    }),
  ),
})

export type PatternsResponse = z.infer<typeof patternsResponseSchema>

// ── /leaves/patterns/team (cached) ──────────────────────
export const cachedEmployeePatternsSchema = z.object({
  employeeId: z.guid(),
  employeeName: z.string().default(''),
  teamName: z.string().default(''),
  // Parent org-unit ("department") name — feeds the Rhythm board's
  // all/department/team scope filter, mirroring the Team roster. Empty when the
  // team has no parent unit; defaulted so older cached responses still parse.
  departmentName: z.string().default(''),
  patterns: z.array(
    z.object({
      kind: PatternKind,
      severity: z.enum(['low', 'medium', 'high']),
      possibleSignal: z.string(),
      recommendation: z.string(),
      evidence: z.object({
        count: z.number().int(),
        windowDays: z.number().int(),
        sampleLeaveIds: z.array(z.guid()),
      }),
      // Wave 2 (2026-05-20). Defaults keep the schema backwards-compatible
      // for any cached response written before the catalog was extended.
      //
      // relevantKinds: array of LeaveType strings the chip should appear
      // on, plus the optional "emergency_flag" sentinel for patterns that
      // gate on isEmergency. Empty array == wildcard.
      //
      // evidenceUnit: noun the chip uses to render the count, e.g.
      // "sick days", "half-days", "emergencies".
      relevantKinds: z.array(z.string()).default([]),
      evidenceUnit: z.string().default('occurrences'),
      // True when ILMU judged the pattern to be calendar-driven rather than
      // a genuine behavioural signal (e.g. long-weekend overlap with a public
      // holiday). Surfaced in the collapsible "ILMU suppressed" section on
      // the manager Leave Patterns page; excluded from the main list.
      // Default false keeps older cached responses parsing cleanly.
      suppressed: z.boolean().default(false),
      // Sourced from ai.pattern_catalog. `bucket` drives the pattern-card
      // accent + pill on the Rhythm board; `performanceRisk` surfaces the
      // "Perf flag" chip. Defaults keep older cached responses parsing.
      bucket: z.enum(['performance', 'wellbeing']).default('wellbeing'),
      performanceRisk: z.boolean().default(false),
    }),
  ),
  detectedAt: z.string().nullable(),
  // Annual-leave snapshot for the Rhythm board's "days left" figure + bar.
  // `annualLeft` = entitlement − used − pending (accrual-clamped);
  // `annualTotal` = the year's entitlement slice. Null when the employee has
  // no annual balance row for the current year.
  annualLeft: z.number().nullable().default(null),
  annualTotal: z.number().nullable().default(null),
  // Per-pattern live "personal data point" values, keyed by pattern code →
  // { metricKey: value } — the SAME numbers the employee sees on their home
  // HealthBarCard (sick_days, unpaid_days, used_days, …). Lets the Rhythm board
  // render the personalised data-point sentence. Empty for patterns without a
  // dynamic collapsed line.
  patternMetrics: z
    .record(z.string(), z.record(z.string(), z.number()))
    .default({}),
  // Replacement-leave nudge: the soonest-expiring batch of banked replacement
  // days. `replacementDays` = days sharing the earliest expiry; `replacementExpiresAt`
  // = that ISO date. 0 / null when the employee has no active replacement grants.
  replacementDays: z.number().default(0),
  replacementExpiresAt: z.string().nullable().default(null),
  // Policy v1.0 §2.3 — employee health-bar projection. Surfaced on the
  // manager Leave Patterns page so the score/band chip renders next to
  // the pattern count, and the sort can tiebreak on band.
  healthBar: z
    .object({
      score: z.number().int().nonnegative(),
      band: z.enum(['low', 'medium', 'high']),
      contributingPatterns: z.array(z.string()).default([]),
    })
    .nullable()
    .optional(),
})
export type CachedEmployeePatterns = z.infer<typeof cachedEmployeePatternsSchema>

export const cachedTeamPatternsResponseSchema = z.object({
  items: z.array(cachedEmployeePatternsSchema),
})
export type CachedTeamPatternsResponse = z.infer<typeof cachedTeamPatternsResponseSchema>

// ── Coming Up · Next Step (spec 2026-05-26) ───────────────────────────

export type NextActionIntent =
  | 'active_blackout'
  | 'imminent_blackout'
  | 'tight_capacity'
  | 'imminent_holiday'
  | 'smooth'

export type ViewerRoleBucket = 'employee' | 'manager' | 'hr_director'

export interface NextActionFacts {
  blackoutName?: string
  blackoutStartIso?: string
  blackoutStartShort?: string
  blackoutEndIso?: string
  blackoutEndShort?: string
  blackoutDaysAway?: number
  blackoutCount?: number
  peakIso?: string
  peakShort?: string
  peakOut?: number
  peakPct?: number
  pendingOverlapCount?: number
  holidayName?: string
  holidayIso?: string
  holidayShort?: string
  holidayDaysAway?: number
}

export interface NextActionCta {
  label: string
  href: string
}

export interface NextActionResponse {
  intent: NextActionIntent
  roleBucket: ViewerRoleBucket
  facts: NextActionFacts
  template: string
  prose: string
  isLlmRephrased: boolean
  cta: NextActionCta | null
  fingerprint: string
}
